import { useRef, useState } from 'react'
import {
	Controller,
	type FieldErrors,
	type UseFormRegister,
	type UseFormSetValue,
	useFieldArray,
	useForm,
} from 'react-hook-form'
import { Button } from '../Button'
import { Modal } from '../Modal'
import { Text } from '../Text'
import { Toggle } from '../Toggle'
import { isConfiguredRoleMapping } from './helpers'
import type {
	DealTeamIntegrationLike,
	DealTeamManualMemberInput,
	DealTeamOrgUser,
	DealTeamTriggerCsvInput,
	DealTeamTriggerManualInput,
	DealTeamTriggerResult,
} from './types'

interface DealTeamTriggerDialogProps {
	isOpen: boolean
	onClose: () => void
	integration: DealTeamIntegrationLike
	orgUsers: DealTeamOrgUser[]
	userSearch: string
	onUserSearchChange: (q: string) => void
	triggerManual: (input: DealTeamTriggerManualInput) => Promise<DealTeamTriggerResult>
	triggerCsv: (input: DealTeamTriggerCsvInput) => Promise<DealTeamTriggerResult>
	onShowToast: (message: string, level?: 'error' | 'info') => void
	onTriggerSuccess?: (result: DealTeamTriggerResult) => void
}

type InputMode = 'form' | 'csv'
type DialogView = 'input' | 'result'

interface MemberRow {
	mode: 'existing' | 'new'
	userId: string
	email: string
	firstName: string
	lastName: string
	crmRoleValue: string
}

interface TriggerFormValues {
	cycleName: string
	members: MemberRow[]
	autoLaunch: boolean
}

const defaultMember: MemberRow = {
	mode: 'existing',
	userId: '',
	email: '',
	firstName: '',
	lastName: '',
	crmRoleValue: '',
}

function parseManualMembers(members: MemberRow[]): { members: DealTeamManualMemberInput[]; error?: string } {
	const parsedMembers: DealTeamManualMemberInput[] = []

	for (const member of members) {
		const crmRoleValue = member.crmRoleValue.trim()

		if (member.mode === 'existing') {
			if (!member.userId) {
				return { members: [], error: 'Select an existing user for each existing-user row.' }
			}
			parsedMembers.push({ userId: member.userId, crmRoleValue })
			continue
		}

		if (!member.email || !member.firstName || !member.lastName) {
			return { members: [], error: 'New members require email, first name, and last name.' }
		}

		parsedMembers.push({
			email: member.email.trim(),
			firstName: member.firstName.trim(),
			lastName: member.lastName.trim(),
			crmRoleValue,
		})
	}

	return { members: parsedMembers }
}

interface MemberCardProps {
	index: number
	canRemove: boolean
	memberMode: MemberRow['mode']
	orgUsers: DealTeamOrgUser[]
	roleOptions: string[]
	hasConfiguredRoles: boolean
	errors: FieldErrors<TriggerFormValues>
	register: UseFormRegister<TriggerFormValues>
	setValue: UseFormSetValue<TriggerFormValues>
	onRemove: (index: number) => void
}

function MemberCard({
	index,
	canRemove,
	memberMode,
	orgUsers,
	roleOptions,
	hasConfiguredRoles,
	errors,
	register,
	setValue,
	onRemove,
}: Readonly<MemberCardProps>) {
	return (
		<div className='border border-neutral-200 rounded-lg p-4 bg-neutral-50'>
			<div className='flex items-center justify-between mb-3'>
				<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
					Member {index + 1}
				</Text>
				{canRemove && (
					<button
						type='button'
						onClick={() => onRemove(index)}
						className='text-xs text-red-500 hover:text-red-700 font-medium transition-colors'>
						Remove
					</button>
				)}
			</div>

			<div className='grid grid-cols-2 gap-3'>
				<div className='col-span-2 flex gap-4'>
					<label className='inline-flex items-center gap-2 text-xs text-neutral-700'>
						<input
							type='radio'
							checked={memberMode === 'existing'}
							onChange={() => {
								setValue(`members.${index}.mode`, 'existing')
								setValue(`members.${index}.email`, '')
								setValue(`members.${index}.firstName`, '')
								setValue(`members.${index}.lastName`, '')
							}}
						/>
						<span>Existing user</span>
					</label>
					<label className='inline-flex items-center gap-2 text-xs text-neutral-700'>
						<input
							type='radio'
							checked={memberMode === 'new'}
							onChange={() => {
								setValue(`members.${index}.mode`, 'new')
								setValue(`members.${index}.userId`, '')
							}}
						/>
						<span>New member</span>
					</label>
				</div>

				{memberMode === 'existing' ? (
					<div className='col-span-2 flex flex-col gap-1'>
						<span className='text-xs font-medium text-neutral-600'>
							Existing User <span className='text-red-500'>*</span>
						</span>
						<select
							{...register(`members.${index}.userId`)}
							className='rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm text-neutral-900 bg-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'>
							<option value=''>Select user...</option>
							{orgUsers.map((user) => (
								<option key={user.id} value={user.id}>
									{user.firstName} {user.lastName} ({user.email})
								</option>
							))}
						</select>
					</div>
				) : (
					<>
						<div className='flex flex-col gap-1'>
							<span className='text-xs font-medium text-neutral-600'>
								Email <span className='text-red-500'>*</span>
							</span>
							<input
								{...register(`members.${index}.email`)}
								type='email'
								placeholder='name@example.com'
								className='rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
							/>
							{errors.members?.[index]?.email && (
								<span className='text-xs text-red-500'>{errors.members[index]?.email?.message}</span>
							)}
						</div>

						<div className='flex flex-col gap-1'>
							<span className='text-xs font-medium text-neutral-600'>
								First Name <span className='text-red-500'>*</span>
							</span>
							<input
								{...register(`members.${index}.firstName`)}
								type='text'
								placeholder='First name'
								className='rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
							/>
							{errors.members?.[index]?.firstName && (
								<span className='text-xs text-red-500'>{errors.members[index]?.firstName?.message}</span>
							)}
						</div>

						<div className='flex flex-col gap-1'>
							<span className='text-xs font-medium text-neutral-600'>
								Last Name <span className='text-red-500'>*</span>
							</span>
							<input
								{...register(`members.${index}.lastName`)}
								type='text'
								placeholder='Last name'
								className='rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
							/>
							{errors.members?.[index]?.lastName && (
								<span className='text-xs text-red-500'>{errors.members[index]?.lastName?.message}</span>
							)}
						</div>
					</>
				)}

				{hasConfiguredRoles && (
					<div className='col-span-2 flex flex-col gap-1'>
						<span className='text-xs font-medium text-neutral-600'>Deal Team Role</span>
						<select
							{...register(`members.${index}.crmRoleValue`)}
							className='rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm text-neutral-900 bg-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'>
							<option value=''>Template default</option>
							{roleOptions.map((roleValue) => (
								<option key={roleValue} value={roleValue}>
									{roleValue}
								</option>
							))}
						</select>
					</div>
				)}
			</div>
		</div>
	)
}

interface TriggerResultViewProps {
	result: DealTeamTriggerResult | null
	errorMessage: string | null
	onClose: () => void
	onRetry: () => void
}

function TriggerResultView({ result, errorMessage, onClose, onRetry }: Readonly<TriggerResultViewProps>) {
	return (
		<div className='flex flex-col items-center justify-center py-8 gap-4'>
			{result ? (
				<>
					<div className='w-16 h-16 rounded-full bg-green-100 flex items-center justify-center'>
						<svg
							className='w-8 h-8 text-green-600'
							fill='none'
							viewBox='0 0 24 24'
							stroke='currentColor'
							strokeWidth={2}
							aria-hidden='true'>
							<path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
						</svg>
					</div>
					<Text variant='medium' weight='semibold' className='text-neutral-900'>
						Workflow triggered successfully
					</Text>
					<Text variant='small' className='text-neutral-600'>
						Cycle created: {result.cycleName}
					</Text>
					<Button variant='secondary' onClick={onClose}>
						Close
					</Button>
				</>
			) : (
				<>
					<div className='w-16 h-16 rounded-full bg-red-100 flex items-center justify-center'>
						<svg
							className='w-8 h-8 text-red-600'
							fill='none'
							viewBox='0 0 24 24'
							stroke='currentColor'
							strokeWidth={2}
							aria-hidden='true'>
							<path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
						</svg>
					</div>
					<Text variant='medium' weight='semibold' className='text-neutral-900'>
						Workflow trigger failed
					</Text>
					{errorMessage && (
						<div className='w-full bg-red-50 border border-red-200 rounded-lg px-4 py-3'>
							<Text variant='small' className='text-red-700'>
								{errorMessage}
							</Text>
						</div>
					)}
					<div className='flex gap-3'>
						<Button variant='secondary' onClick={onClose}>
							Close
						</Button>
						<Button variant='primary' onClick={onRetry}>
							Try Again
						</Button>
					</div>
				</>
			)}
		</div>
	)
}

export function DealTeamTriggerDialog({
	isOpen,
	onClose,
	integration,
	orgUsers,
	userSearch,
	onUserSearchChange,
	triggerManual,
	triggerCsv,
	onShowToast,
	onTriggerSuccess,
}: DealTeamTriggerDialogProps) {
	const [inputMode, setInputMode] = useState<InputMode>('form')
	const [dialogView, setDialogView] = useState<DialogView>('input')
	const [result, setResult] = useState<DealTeamTriggerResult | null>(null)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [csvContent, setCsvContent] = useState<string>('')
	const [csvFileName, setCsvFileName] = useState<string>('')
	const [isPending, setIsPending] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const configuredRoleMappings = Array.isArray(integration.crmRoleMappings)
		? integration.crmRoleMappings.filter(isConfiguredRoleMapping)
		: []
	const hasConfiguredRoles = configuredRoleMappings.length > 0
	const roleOptions = configuredRoleMappings.map((mapping) => mapping.crmRoleValue)

	const {
		register,
		control,
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm<TriggerFormValues>({
		defaultValues: {
			cycleName: '',
			members: [{ ...defaultMember }],
			autoLaunch: false,
		},
	})

	const { fields, append, remove } = useFieldArray({ control, name: 'members' })
	const memberValues = watch('members')

	const handleClose = () => {
		reset({ cycleName: '', members: [{ ...defaultMember }], autoLaunch: false })
		setInputMode('form')
		setDialogView('input')
		setResult(null)
		setErrorMessage(null)
		setCsvContent('')
		setCsvFileName('')
		if (fileInputRef.current) fileInputRef.current.value = ''
		onClose()
	}

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return

		const reader = new FileReader()
		reader.onload = (event) => {
			const content = event.target?.result
			if (typeof content === 'string') {
				setCsvFileName(file.name)
				setCsvContent(content)
			} else {
				onShowToast('Failed to read file content. Please try a different file.', 'error')
				setCsvFileName('')
				setCsvContent('')
			}
		}
		reader.onerror = () => {
			onShowToast('Failed to read the selected file. Please try again or use a different file.', 'error')
			setCsvFileName('')
			setCsvContent('')
			if (fileInputRef.current) fileInputRef.current.value = ''
		}
		reader.readAsText(file)
	}

	const handleSuccess = (data: DealTeamTriggerResult) => {
		setResult(data)
		setErrorMessage(null)
		setDialogView('result')
		setIsPending(false)
		onTriggerSuccess?.(data)
	}

	const handleError = (err: unknown) => {
		setErrorMessage(err instanceof Error ? err.message : 'Workflow trigger failed')
		setDialogView('result')
		setIsPending(false)
	}

	const onSubmit = handleSubmit(async (data) => {
		if (inputMode === 'form') {
			const parsed = parseManualMembers(data.members)
			if (parsed.error) {
				onShowToast(parsed.error, 'error')
				return
			}
			setIsPending(true)
			try {
				const r = await triggerManual({
					cycleName: data.cycleName,
					members: parsed.members,
					autoLaunch: data.autoLaunch,
				})
				handleSuccess(r)
			} catch (err) {
				handleError(err)
			}
		} else {
			if (!csvContent) {
				onShowToast('Please select a CSV file', 'error')
				return
			}
			setIsPending(true)
			try {
				const r = await triggerCsv({
					cycleName: data.cycleName,
					csvContent,
					autoLaunch: data.autoLaunch,
				})
				handleSuccess(r)
			} catch (err) {
				handleError(err)
			}
		}
	})

	const handleRetry = () => {
		setDialogView('input')
		setResult(null)
		setErrorMessage(null)
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title={`Trigger Workflow: ${integration.name}`}
			className='!w-[700px] md:!w-[800px]'>
			{dialogView === 'result' ? (
				<TriggerResultView result={result} errorMessage={errorMessage} onClose={handleClose} onRetry={handleRetry} />
			) : (
				<form onSubmit={onSubmit} className='flex flex-col gap-5'>
					<div className='flex flex-col gap-1.5'>
						<label className='text-sm font-medium text-neutral-700' htmlFor='trigger-cycleName'>
							Cycle Name <span className='text-red-500'>*</span>
						</label>
						<input
							id='trigger-cycleName'
							{...register('cycleName', { required: 'Cycle name is required' })}
							type='text'
							placeholder='Enter cycle name'
							className='w-full rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
						/>
						{errors.cycleName && (
							<Text variant='xsmall' className='text-red-500'>
								{errors.cycleName.message}
							</Text>
						)}
					</div>

					<div className='flex gap-1 p-1 bg-neutral-100 rounded-lg w-fit'>
						<button
							type='button'
							onClick={() => setInputMode('form')}
							className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
								inputMode === 'form' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
							}`}>
							Form
						</button>
						<button
							type='button'
							onClick={() => setInputMode('csv')}
							className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
								inputMode === 'csv' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
							}`}>
							CSV Upload
						</button>
					</div>

					{inputMode === 'form' && (
						<div className='flex flex-col gap-3'>
							<div className='rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3'>
								<Text variant='xsmall' className='text-neutral-600'>
									Manual trigger uses the selected integration configuration. Role options are restricted to this
									integration's configured Deal Team roles.
								</Text>
							</div>
							{!hasConfiguredRoles && (
								<div className='rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3'>
									<Text variant='xsmall' className='text-neutral-600'>
										No Deal Team roles configured — members will be assigned the template's default role.
									</Text>
								</div>
							)}
							<div className='flex flex-col gap-1'>
								<label className='text-xs font-medium text-neutral-600' htmlFor='manual-user-search'>
									Search Existing Org Users
								</label>
								<input
									id='manual-user-search'
									type='text'
									value={userSearch}
									onChange={(e) => onUserSearchChange(e.target.value)}
									placeholder='Search by name, email, or external ID'
									className='rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
								/>
							</div>
							<div className='flex items-center justify-between'>
								<Text variant='small' weight='semibold' className='text-neutral-700'>
									Team Members
								</Text>
								<Button type='button' variant='secondary' size='small' onClick={() => append({ ...defaultMember })}>
									+ Add Member
								</Button>
							</div>

							<div className='flex flex-col gap-3 max-h-[320px] overflow-y-auto pr-1'>
								{fields.map((field, index) => (
									<MemberCard
										key={field.id}
										index={index}
										canRemove={fields.length > 1}
										memberMode={memberValues?.[index]?.mode ?? 'existing'}
										orgUsers={orgUsers}
										roleOptions={roleOptions}
										hasConfiguredRoles={hasConfiguredRoles}
										errors={errors}
										register={register}
										setValue={setValue}
										onRemove={remove}
									/>
								))}
							</div>
						</div>
					)}

					{inputMode === 'csv' && (
						<div className='flex flex-col gap-3'>
							<div className='border-2 border-dashed border-neutral-300 rounded-lg p-6 flex flex-col items-center gap-3'>
								<svg
									className='w-10 h-10 text-neutral-400'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'
									strokeWidth={1.5}
									aria-hidden='true'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										d='M9 13.5l3 3m0 0l3-3m-3 3v-6m1.06-4.19l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z'
									/>
								</svg>
								<div className='text-center'>
									<Text variant='small' weight='semibold' className='text-neutral-700'>
										{csvFileName ? csvFileName : 'Upload CSV File'}
									</Text>
									{!csvFileName && (
										<Text variant='xsmall' className='text-neutral-500 mt-1'>
											Click to select a .csv file
										</Text>
									)}
								</div>
								<input
									ref={fileInputRef}
									type='file'
									accept='.csv'
									onChange={handleFileChange}
									className='hidden'
									id='csv-file-input'
								/>
								<Button type='button' variant='secondary' size='small' onClick={() => fileInputRef.current?.click()}>
									{csvFileName ? 'Change File' : 'Select File'}
								</Button>
							</div>

							<div className='bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3'>
								<Text variant='xsmall' weight='semibold' className='text-neutral-600 mb-1'>
									Expected columns:
								</Text>
								<Text variant='xsmall' className='text-neutral-500 font-mono'>
									Email, FirstName, LastName, ExternalId, DirectReportTo, SecondaryReportTo, CoManagers, Role
								</Text>
							</div>
						</div>
					)}

					<div className='flex items-center justify-between py-3 border-t border-neutral-100'>
						<div>
							<Text variant='small' weight='semibold' className='text-neutral-700'>
								Auto-launch Cycle
							</Text>
							<Text variant='xsmall' className='text-neutral-500'>
								Automatically launch the cycle after creation
							</Text>
						</div>
						<Controller
							control={control}
							name='autoLaunch'
							render={({ field }) => <Toggle checked={field.value} onCheckedChange={field.onChange} />}
						/>
					</div>

					<div className='flex justify-end gap-3 pt-4 border-t border-neutral-100'>
						<Button type='button' variant='secondary' onClick={handleClose} disabled={isPending}>
							Cancel
						</Button>
						<Button type='submit' variant='primary' disabled={isPending}>
							{isPending ? 'Triggering...' : 'Trigger Workflow'}
						</Button>
					</div>
				</form>
			)}
		</Modal>
	)
}
